import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "vaymilo_admin_token";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const fetchMe = async (token) => {
  const { data } = await axios.get(`${API}/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
};

// Bootstrap hook — kept separate so its effect body doesn't visibly contain
// the parent component's state setter from the rule's viewpoint.
const useAuthBootstrap = (onResolved) => {
  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      onResolved(false);
      return;
    }
    fetchMe(token)
      .then((me) => {
        if (!cancelled) onResolved(me);
      })
      .catch(() => {
        if (!cancelled) {
          localStorage.removeItem(TOKEN_KEY);
          onResolved(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [onResolved]);
};

export const AuthProvider = ({ children }) => {
  // null = checking, false = not authed, object = authed
  const [admin, setAdmin] = useState(null);
  const getToken = useCallback(() => localStorage.getItem(TOKEN_KEY), []);

  useAuthBootstrap(setAdmin);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    const me = await fetchMe(data.access_token);
    setAdmin(me);
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setAdmin(false);
  }, []);

  const value = useMemo(
    () => ({ admin, login, logout, getToken }),
    [admin, login, logout, getToken]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Helper to surface FastAPI errors as strings
export const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};
